/**
 * Banker Case Detail — view case + submit offer + upload LO PDF.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import bankerApi from '../../lib/bankerApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, FileUp, Loader2, CheckCircle2 } from 'lucide-react';

export default function BankerCaseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    rate_percent: '',
    loan_amount: '',
    tenure_years: 30,
    lock_in_years: 0,
    notes: '',
    valid_until: '',
  });
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await bankerApi.get(`/banker/cases/${id}`);
      setC(r.data);
      // V4.25.10 — Pre-fill the form from THIS banker's existing saved offer
      // (rate / amount / tenure / lock-in / valid-until / notes) so reopening
      // an already-submitted (or approved) case shows the real values instead
      // of blank fields. Falls back to the requested amount / preferred tenure
      // only when no meaningful offer exists yet (i.e. the waiting_bank
      // placeholder, which has no rate/amount).
      const off = (r.data.offers || [])[0];
      if (off && (off.rate_percent || off.loan_amount)) {
        setForm({
          rate_percent: off.rate_percent ?? '',
          loan_amount: off.loan_amount ?? '',
          tenure_years: off.tenure_years ?? r.data.tenure_pref_years ?? 30,
          lock_in_years: off.lock_in_years ?? 0,
          notes: off.notes || '',
          valid_until: (off.valid_until || '').slice(0, 10),
        });
      } else {
        setForm(f => ({
          ...f,
          loan_amount: f.loan_amount || r.data.loan_amount_requested || '',
          tenure_years: f.tenure_years || r.data.tenure_pref_years || 30,
        }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load case'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async () => {
    if (!form.rate_percent || !form.loan_amount) {
      toast.error('Rate and Loan Amount required'); return;
    }
    setBusy(true);
    try {
      await bankerApi.post(`/banker/cases/${id}/offer`, {
        rate_percent: parseFloat(form.rate_percent),
        loan_amount: parseFloat(form.loan_amount),
        tenure_years: parseInt(form.tenure_years),
        lock_in_years: parseInt(form.lock_in_years) || 0,
        notes: form.notes || null,
        valid_until: form.valid_until || null,
      });
      toast.success('Offer approved');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  const uploadLo = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('PDF only'); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await bankerApi.post(`/banker/cases/${id}/offer/upload-lo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Letter of Offer uploaded');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadBusy(false); e.target.value = ''; }
  };

  if (loading) return <p className="text-gray-500">Loading…</p>;
  if (!c) return <p className="text-gray-500">Case not found.</p>;

  const myOffer = (c.offers || [])[0];
  // V4.24.39 — Banker "Update Offer" now writes `approved` directly.
  // Keep the form editable until the case reaches a terminal state — the
  // banker must be able to revise rate/tenure after a first approval.
  const _terminalCaseStates = ['offer_accepted', 'lo_meetup', 'lo_signed',
                                'approved', 'drawdown', 'spa_signed', 'closed'];
  const offerLocked = myOffer && (
    ['rejected', 'superseded', 'drawdown'].includes(myOffer.status) ||
    _terminalCaseStates.includes(c.status)
  );

  return (
    <div className="space-y-4" data-testid="banker-case-detail-page">
      <Link to="/banker/inbox" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to inbox
      </Link>

      <div className="bg-white border rounded-xl p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold">{c.case_ref}</h1>
          {/* V4.24.89 — Banker Portal pill now reflects the BANKER's OWN
              offer status (approved / waiting_bank / rejected …), not
              the case-level status. The banker cares about "did my
              offer land?" — the case status `offer_accepted` was
              confusing because it looked like the customer had already
              accepted, when in reality it just means "the banker
              submitted an approved offer". */}
          {(() => {
            const myOfferForBadge = (c.offers || [])[0];
            const st = myOfferForBadge?.status || c.status;
            const styles = {
              approved:     'bg-emerald-100 text-emerald-700',
              accepted:     'bg-emerald-600 text-white',
              waiting_bank: 'bg-amber-100 text-amber-700',
              reviewing:    'bg-amber-100 text-amber-700',
              rejected:     'bg-red-100 text-red-700',
              superseded:   'bg-slate-100 text-slate-500',
            };
            const labels = {
              approved:     'approved',
              accepted:     'accepted',
              waiting_bank: 'reviewing',
              reviewing:    'reviewing',
              rejected:     'rejected',
              superseded:   'superseded',
            };
            return (
              <Badge className={styles[st] || 'bg-violet-100 text-violet-700'}
                     data-testid="banker-case-status-pill">
                {labels[st] || st}
              </Badge>
            );
          })()}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-500">Customer:</span> <span className="font-medium">{c.customer_name || '—'}</span></div>
          <div><span className="text-gray-500">NRIC:</span> <span className="font-mono">{c.customer_ic || '—'}</span></div>
          <div><span className="text-gray-500">Phone:</span> {c.customer_phone || '—'}</div>
          <div><span className="text-gray-500">Email:</span> {c.customer_email || '—'}</div>
          <div><span className="text-gray-500">Property:</span> {c.property_name || '—'}</div>
          <div><span className="text-gray-500">Property Price:</span> {c.property_price ? `RM ${Number(c.property_price).toLocaleString('en-MY')}` : '—'}</div>
          <div><span className="text-gray-500">Requested Loan:</span> {c.loan_amount_requested ? `RM ${Number(c.loan_amount_requested).toLocaleString('en-MY')}` : '—'}</div>
          <div><span className="text-gray-500">Preferred Tenure:</span> {c.tenure_pref_years || '—'} yrs</div>
          <div><span className="text-gray-500">Employment:</span> {c.profile?.employment_type || '—'}</div>
          <div><span className="text-gray-500">Monthly Income:</span> {c.profile?.monthly_income ? `RM ${Number(c.profile.monthly_income).toLocaleString('en-MY')}` : '—'}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="banker-offer-form">
        <h2 className="font-semibold flex items-center gap-2">
          {offerLocked ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Your Offer</> : 'Submit / Update Offer'}
        </h2>
        {offerLocked && (
          <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-sm">
            Your offer is in status <strong>{myOffer.status}</strong> — further edits disabled.
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Rate % *</Label>
            <Input type="number" step="0.01" value={form.rate_percent}
              disabled={offerLocked}
              onChange={e => setForm({ ...form, rate_percent: e.target.value })}
              data-testid="banker-offer-rate" />
          </div>
          <div>
            <Label>Loan Amount (RM) *</Label>
            <Input type="number" value={form.loan_amount}
              disabled={offerLocked}
              onChange={e => setForm({ ...form, loan_amount: e.target.value })}
              data-testid="banker-offer-amount" />
          </div>
          <div>
            <Label>Tenure (years) *</Label>
            <Input type="number" value={form.tenure_years}
              disabled={offerLocked}
              onChange={e => setForm({ ...form, tenure_years: e.target.value })}
              data-testid="banker-offer-tenure" />
          </div>
          <div>
            <Label>Lock-in (years)</Label>
            <Input type="number" value={form.lock_in_years}
              disabled={offerLocked}
              onChange={e => setForm({ ...form, lock_in_years: e.target.value })}
              data-testid="banker-offer-lockin" />
          </div>
          <div>
            <Label>Valid Until</Label>
            <Input type="date" value={form.valid_until}
              disabled={offerLocked}
              onChange={e => setForm({ ...form, valid_until: e.target.value })}
              data-testid="banker-offer-valid-until" />
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea rows={2} value={form.notes}
            disabled={offerLocked}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            data-testid="banker-offer-notes" />
        </div>
        {!offerLocked && (
          <Button onClick={submit} disabled={busy} data-testid="banker-offer-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (myOffer ? 'Update Offer' : 'Submit Offer')}
          </Button>
        )}

        {myOffer && (
          <div className="pt-3 mt-3 border-t space-y-2">
            <h3 className="text-sm font-semibold">Letter of Offer (PDF)</h3>
            {myOffer.lo_pdf_url ? (
              <a
                href={myOffer.lo_pdf_url.startsWith('http') ? myOffer.lo_pdf_url : `${process.env.REACT_APP_BACKEND_URL}/api${myOffer.lo_pdf_url}`}
                target="_blank" rel="noreferrer"
                className="text-indigo-600 hover:underline text-sm"
                data-testid="banker-lo-link"
              >View uploaded LO →</a>
            ) : (
              <p className="text-xs text-gray-500">No LO uploaded yet.</p>
            )}
            <input type="file" accept="application/pdf" ref={fileRef} onChange={uploadLo} className="hidden" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploadBusy || offerLocked}
              data-testid="banker-lo-upload"
            >
              {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileUp className="w-4 h-4 mr-1" /> Upload LO PDF</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
